import type { CreateOrgKeyRequest } from '@compass/client/types/org_key';
import { orgKeyService } from '@services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/services/query.config';

/** Fetches the org keys of one org; idle while `organizationId` is null. */
export const useOrgKeys = (organizationId: string | null) =>
  useQuery({
    queryKey: queryKeys.orgKeys.list(organizationId ?? ''),
    queryFn: () => orgKeyService.listOrgKeys({ organization_id: organizationId ?? '' }),
    enabled: !!organizationId,
    staleTime: 30_000,
  });

export const useCreateOrgKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateOrgKeyRequest) => orgKeyService.createOrgKey(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgKeys.all }),
  });
};

export const useRotateOrgKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: { organization_id: string; org_key_id: string }) =>
      orgKeyService.rotateOrgKey(req),
    // onSettled, not onSuccess: a failed call (e.g. the key is already gone)
    // must still refresh the list so a stale row doesn't linger.
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgKeys.all }),
  });
};

export const useDeleteOrgKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: { organization_id: string; org_key_id: string }) =>
      orgKeyService.deleteOrgKey(req),
    // onSettled, not onSuccess: a failed call (e.g. the key is already gone)
    // must still refresh the list so a stale row doesn't linger.
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.orgKeys.all }),
  });
};
